import React, { useRef, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ContentCopy } from '@mui/icons-material';
import { AuthService } from '../services/AuthService';
import { environment } from '../config/environment';

/** Claude Code 에 parking-manage MCP 를 등록하는 명령 — 모든 폴더에서 쓰도록 user 범위로 넣는다. */
export function mcpAddCommand(apiBaseUrl: string, token: string): string {
  const url = `${apiBaseUrl.replace(/\/+$/, '')}/v0.1/mcp`;
  return `claude mcp add --scope user --transport http parking-manage ${url} --header "Authorization: Bearer ${token}"`;
}

interface McpConnectDialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Claude Code 연결 — MCP 전용 토큰을 받아 등록 명령을 만든다. 토큰은 내 계정 권한 그대로이고
 * 90일 동안 쓴다(웹에서 로그아웃해도 유지). 명령을 터미널에 붙여 넣으면 끝.
 */
const McpConnectDialog: React.FC<McpConnectDialogProps> = ({ open, onClose }) => {
  const [command, setCommand] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const fieldRef = useRef<HTMLInputElement>(null);

  const issue = async () => {
    setBusy(true);
    setError('');
    setCopied(false);
    try {
      const { token, expires_at } = await AuthService.issueMcpToken();
      setCommand(mcpAddCommand(environment.API_BASE_URL, token));
      setExpiresAt(new Date(expires_at).toLocaleDateString('ko-KR'));
    } catch (e: any) {
      setError(e?.message || '토큰을 받지 못했습니다');
    } finally {
      setBusy(false);
    }
  };

  // http 로 연 화면은 clipboard API 가 없다 — 그때는 글자를 선택해 execCommand 로 복사한다.
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(command);
    } catch {
      fieldRef.current?.select();
      document.execCommand('copy');
    }
    setCopied(true);
  };

  const close = () => {
    setCommand('');
    setCopied(false);
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle>Claude Code 연결 (MCP)</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Claude Code 에서 현장 상태·QA·장애 이력·배포를 대화로 조회하고 바꿀 수 있게 연결합니다. 내 계정 권한
            그대로 동작합니다(뷰어는 조회만). 아래 명령을 터미널에 한 번 붙여 넣고, Claude Code 를 새로 열면 됩니다.
          </Typography>
          {!command && (
            <Button variant="contained" onClick={issue} disabled={busy} sx={{ alignSelf: 'flex-start', minHeight: 44 }}>
              {busy ? '만드는 중…' : '연결 명령 만들기'}
            </Button>
          )}
          {error && <Alert severity="error">{error}</Alert>}
          {command && (
            <>
              <TextField
                inputRef={fieldRef}
                value={command}
                multiline
                fullWidth
                InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: 13 } }}
                onFocus={(e) => e.target.select()}
              />
              <Alert severity="warning">
                명령에 든 토큰은 비밀번호와 같습니다. 다른 사람과 공유하지 마세요. {expiresAt} 까지 쓸 수 있고, 지나면 여기서
                다시 만들어 같은 명령을 다시 실행하면 됩니다(먼저 <code>claude mcp remove parking-manage</code>).
              </Alert>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        {command && (
          <Button startIcon={<ContentCopy />} onClick={copy} sx={{ minHeight: 44 }}>
            {copied ? '복사됨' : '명령 복사'}
          </Button>
        )}
        <Button onClick={close} sx={{ minHeight: 44 }}>
          닫기
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default McpConnectDialog;
